import { FormikProps } from "formik";
import { Box, Flex } from "@chakra-ui/react";
import BinarySplitView from "@/features/ui/binary-split-view";
import InvoiceFileFormControl from "@/features/invoices/order-confirmation-invoices/invoice-file-form-control";
import React, { useCallback, useEffect } from "react";
import { useBeforeUnload } from "react-router-dom";
import useIdentity from "@/features/ui/hooks/use-identity";
import {
  OrderConfirmationInvoiceFormFieldsModal
} from "@/features/invoices/order-confirmation-invoices/upload-order-confirmation-invoice-page-modal";
import OrderConfirmationInvoiceInformationFormControlModal
  from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-information-form-control-modal";
import OrderConfirmationInvoiceFigureFormControlModal
  from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-figure-form-control-modal";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import InvoicePaymentsPanel from "@/features/invoices/payment/invoice-payments-panel";

interface OrderConfirmationInvoiceFormModalProps {
  formik: FormikProps<OrderConfirmationInvoiceFormFieldsModal>;
  isEditing?: boolean;
  // 관련 송금 요청 — 프로포마 모달처럼 보여 주고 영수증을 올려 송금 완료 처리한다
  payments?: LightPayment[];
  onReceiptModalClose?: () => void;
}

export default function OrderConfirmationInvoiceFormModal({
  formik,
  isEditing = false,
  payments = [],
  onReceiptModalClose,
}: OrderConfirmationInvoiceFormModalProps) {
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (formik.isSubmitting || (formik.dirty && isEditing)) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [formik.isSubmitting, formik.dirty, isEditing]
  );

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    //수정 취소시 기본값으로 돌리기
    if (!formik.isSubmitting && formik.dirty && !isEditing) {
      formik.resetForm();
    }
  }, [isEditing, formik]);

  // 송금이 하나라도(일부라도) 처리됐으면 잠근다. 송금 요청만 있고 처리 전이면 전부 고칠 수 있다
  // (서버가 바뀐 금액·환율로 자동 송금 요청을 다시 만든다).
  const hasRemittance = payments.some(
    (payment) =>
      payment.status !== "CANCELED" &&
      (payment.isRemitted || (payment.remittedAmount?.value ?? 0) > 0)
  );
  const isDisabled =
    isEditing && (hasRemittance || formik.values.status !== "NORMAL");
  const identity = useIdentity();

  return (
    <Box padding={"24px"} overflow={"hidden"}>
      <BinarySplitView
        left={
          <Flex flexDirection={"column"} gap={"24px"}>
            <OrderConfirmationInvoiceInformationFormControlModal
              formik={formik}
              isEditing={isEditing}
              isDisabled={isDisabled}
            />
            <InvoiceFileFormControl
              formik={formik}
              isEditing={isEditing}
              isDisabled={isDisabled}
            />
          </Flex>
        }
        right={
          <Flex flexDirection={"column"} gap={"24px"}>
            {!(
              identity?.company?.type === "BOUTIQUE" && formik.values.id === -1
            ) && (
              <OrderConfirmationInvoiceFigureFormControlModal
                formik={formik}
                isEditing={isEditing}
                isDisabled={isDisabled}
              />
            )}
            <InvoicePaymentsPanel
              payments={payments}
              onReceiptModalClose={onReceiptModalClose}
            />
          </Flex>
        }
        minLeftWidth={400}
        minRightWidth={200}
      />
    </Box>
  );
}
